import {useMutation, useQueryClient} from '@tanstack/react-query'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import {usePagedList, type PagedListParams} from '@/hooks/common/use-paged-list'
import type {components} from '@/lib/api-schema'

export type CarpetaVisita = components['schemas']['CarpetaVisitaRead']
export type CarpetaAlcanceTipo = components['schemas']['CarpetaAlcanceTipo']
export type CarpetaVisitaEstado = components['schemas']['CarpetaVisitaEstado']
export type CarpetaVisitaDescarga = components['schemas']['CarpetaVisitaDescargaRead']

const CARPETAS_PAGE_SIZE = 20

function base(businessId: string | undefined) {
  return `/businesses/${businessId}/carpetas-visita`
}

/** Sondea mientras alguna carpeta se arma: en producción el despachador es Cloud Tasks y la
 * fila nace `pendiente`; en dev el POST ya vuelve `listo`. */
export function useCarpetasVisita(businessId: string | undefined, params: PagedListParams) {
  return usePagedList<CarpetaVisita>(
    ['carpetas-visita', businessId],
    businessId ? base(businessId) : undefined,
    params,
    CARPETAS_PAGE_SIZE,
    (query) =>
      query.state.data?.items.some((fila) => fila.estado === 'pendiente' || fila.estado === 'procesando') ? 2000 : false
  )
}

/** Idempotente mientras se arma: pedir dos veces devuelve el job en vuelo. `regenerar`
 * existe porque una carpeta envejece. */
export function useGenerarCarpetaVisita(businessId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: {alcanceTipo: CarpetaAlcanceTipo; alcanceRefId: string | null; regenerar?: boolean}) =>
      (
        await api.post<CarpetaVisita>(
          base(businessId),
          {alcance_tipo: input.alcanceTipo, alcance_ref_id: input.alcanceRefId},
          {params: {regenerar: input.regenerar ?? false}}
        )
      ).data,
    onSuccess: (data) => {
      queryClient.invalidateQueries({queryKey: ['carpetas-visita', businessId]})
      // Con el despachador en línea el aviso ya existe al resolver el POST, y la campana
      // solo se repregunta cada 60 s.
      if (data.estado === 'listo') queryClient.invalidateQueries({queryKey: ['notifications']})
      track('carpeta_visita_generada', {business_id: businessId, alcance_tipo: data.alcance_tipo})
    },
  })
}

/** No es un enlace directo: el GET vuelve a verificar la custodia de salud y registra quién
 * se llevó el documento antes de firmar. */
export function useDescargarCarpetaVisita(businessId: string) {
  return useMutation({
    mutationFn: async (carpetaId: string) =>
      (await api.get<CarpetaVisitaDescarga>(`${base(businessId)}/${carpetaId}/descargar`)).data,
    onSuccess: (data) => {
      // Un ancla y no `window.open`: fuera de la pila del gesto del usuario Safari lo bloquea
      // siempre y Chrome a ratos.
      const enlace = document.createElement('a')
      enlace.href = data.download_url
      enlace.target = '_blank'
      enlace.rel = 'noopener'
      enlace.click()
      track('carpeta_visita_descargada', {business_id: businessId})
    },
  })
}
